import React, { useState } from 'react';
import { cn } from '@/lib/utils';

interface BentoGridItem {
  id: string;
  content: React.ReactNode;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'gradient-border' | '3d-tilt';
}

interface BentoGridProps {
  items: BentoGridItem[];
  className?: string;
  enableHoverEffects?: boolean;
}

const BentoGrid: React.FC<BentoGridProps> = ({ 
  items, 
  className,
  enableHoverEffects = true 
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, itemId: string) => {
    if (!enableHoverEffects) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
    setHoveredId(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredId(null);
  };

  return (
    <div
      className={cn(
        'grid grid-cols-1 md:grid-cols-4 gap-6 auto-rows-[minmax(200px,auto)]',
        enableHoverEffects && 'perspective-container',
        className
      )}
    >
      {items.map((item, index) => {
        // Asymmetric pattern: 1st large (2x2), 2nd-3rd medium (1x1), 4th large (2x1)
        let sizeClass = 'md:col-span-2 md:row-span-2';
        
        if (index === 0) {
          sizeClass = 'md:col-span-2 md:row-span-2';
        } else if (index === 1) {
          sizeClass = 'md:col-span-1 md:row-span-1';
        } else if (index === 2) {
          sizeClass = 'md:col-span-1 md:row-span-1';
        } else if (index === 3) {
          sizeClass = 'md:col-span-2 md:row-span-1';
        }

        const variant = item.variant || (index === 0 ? '3d-tilt' : 'gradient-border');
        const isHovered = hoveredId === item.id;

        return (
          <div
            key={item.id}
            className={cn(
              'bg-card rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-500 group relative overflow-hidden',
              'border border-border/40',
              sizeClass,
              item.className,
              variant === '3d-tilt' && 'card-3d',
              variant === 'gradient-border' && 'animated-gradient-border',
              enableHoverEffects && 'hover:-translate-y-2'
            )}
            style={{ animationDelay: `${index * 100}ms` }}
            onMouseMove={(e) => handleMouseMove(e, item.id)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Animated gradient border */}
            {variant === 'gradient-border' && isHovered && (
              <div className="absolute inset-0 rounded-2xl opacity-100 pointer-events-none">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/10 via-transparent to-accent/10 animate-pulse" />
              </div>
            )}

            {/* Light-trace effect with mouse tracking */}
            {enableHoverEffects && isHovered && (
              <div
                className="absolute inset-0 opacity-40 pointer-events-none"
                style={{
                  background: `radial-gradient(
                    circle at ${mousePosition.x}% ${mousePosition.y}%,
                    hsl(270 70% 45% / 0.15),
                    transparent 80%
                  )`,
                  transition: 'background 0.1s ease-out',
                }}
              />
            )}

            {/* Default light-trace on hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
            </div>

            {/* Shimmer effect */}
            {variant === '3d-tilt' && (
              <div className="absolute inset-0 shimmer-effect pointer-events-none rounded-2xl" />
            )}

            {/* Content */}
            <div className={cn(
              'relative z-10 h-full flex flex-col justify-between',
              variant === '3d-tilt' && 'card-3d-inner'
            )}>
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BentoGrid;
